/* ============================================================
   Bloom theme — theme.js
   Vanilla JS for: variant picker, quantity stepper, accordion,
   purchase-option toggle, AJAX add-to-cart + cart drawer.
   ============================================================ */

/* ---------- Utility: format money ---------- */
function formatMoney(cents) {
  const amount = (cents / 100).toFixed(2);
  return 'R' + amount;
}

/* ---------- Accordion (single-open) ---------- */
class BloomAccordion extends HTMLElement {
  connectedCallback() {
    this.rows = Array.from(this.querySelectorAll('[data-accordion-row]'));
    this.rows.forEach((row) => {
      const header = row.querySelector('[data-accordion-header]');
      header.addEventListener('click', () => this.toggle(row));
    });
  }
  toggle(target) {
    const isOpen = target.getAttribute('data-open') === 'true';
    this.rows.forEach((row) => {
      const open = row === target && !isOpen;
      row.setAttribute('data-open', open ? 'true' : 'false');
      const panel = row.querySelector('[data-accordion-panel]');
      const sign = row.querySelector('[data-accordion-sign]');
      if (panel) panel.hidden = !open;
      if (sign) sign.textContent = open ? '\u2013' : '+';
    });
  }
}
customElements.define('bloom-accordion', BloomAccordion);

/* ---------- Quantity stepper ---------- */
class BloomQuantity extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('[data-quantity-input]');
    this.querySelector('[data-quantity-minus]').addEventListener('click', () => this.step(-1));
    this.querySelector('[data-quantity-plus]').addEventListener('click', () => this.step(1));
  }
  step(dir) {
    const min = parseInt(this.input.min || '1', 10);
    let val = parseInt(this.input.value || '1', 10) + dir;
    if (val < min) val = min;
    this.input.value = val;
    this.input.dispatchEvent(new Event('change', { bubbles: true }));
  }
}
customElements.define('bloom-quantity', BloomQuantity);

/* ---------- Variant picker + purchase options + live total ---------- */
class BloomProductForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form[action*="/cart/add"]');
    this.variantInput = this.querySelector('[data-variant-id]');
    this.priceTarget = this.querySelector('[data-atc-total]');
    this.qtyInput = this.querySelector('[data-quantity-input]');
    this.sellingPlanInput = this.querySelector('[data-selling-plan]');

    try {
      this.variants = JSON.parse(this.querySelector('[data-variants-json]').textContent);
    } catch (e) {
      this.variants = [];
    }
    this.currentVariant = this.variants.find((v) => String(v.id) === String(this.variantInput.value)) || this.variants[0];

    // Scent chips
    this.chips = Array.from(this.querySelectorAll('[data-variant-chip]'));
    this.chips.forEach((chip) => {
      chip.addEventListener('click', () => this.selectVariant(chip.getAttribute('data-variant-chip')));
    });

    // Purchase option cards
    this.planCards = Array.from(this.querySelectorAll('[data-plan-card]'));
    this.planCards.forEach((card) => {
      card.addEventListener('click', () => this.selectPlan(card));
    });

    // Quantity changes
    if (this.qtyInput) {
      this.qtyInput.addEventListener('change', () => this.updateTotal());
    }

    // AJAX submit
    if (this.form) {
      this.form.addEventListener('submit', (e) => this.onSubmit(e));
    }

    this.updateTotal();
  }

  selectVariant(variantId) {
    const variant = this.variants.find((v) => String(v.id) === String(variantId));
    if (!variant) return;
    this.currentVariant = variant;
    this.variantInput.value = variant.id;

    // chip active state
    this.chips.forEach((chip) => {
      chip.setAttribute('data-active', chip.getAttribute('data-variant-chip') === String(variantId) ? 'true' : 'false');
    });

    // update labels
    this.querySelectorAll('[data-current-scent]').forEach((el) => {
      el.textContent = variant.option1 || variant.title;
    });

    // update gallery image if variant has featured media
    if (variant.featured_media && variant.featured_media.id) {
      const gallery = document.querySelector('bloom-gallery');
      if (gallery && gallery.selectByMediaId) gallery.selectByMediaId(variant.featured_media.id);
    }

    // update URL
    const url = new URL(window.location);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url);

    this.updateTotal();
  }

  selectPlan(card) {
    this.planCards.forEach((c) => c.setAttribute('data-active', c === card ? 'true' : 'false'));
    const planId = card.getAttribute('data-plan-card');
    if (this.sellingPlanInput) {
      this.sellingPlanInput.value = planId === 'onetime' ? '' : planId;
    }
    this.updateTotal();
  }

  unitPrice() {
    // selling plan price if available, else variant price
    const activePlan = this.querySelector('[data-plan-card][data-active="true"]');
    if (activePlan && activePlan.getAttribute('data-plan-price')) {
      return parseInt(activePlan.getAttribute('data-plan-price'), 10);
    }
    return this.currentVariant ? this.currentVariant.price : 0;
  }

  updateTotal() {
    if (!this.priceTarget) return;
    const qty = this.qtyInput ? parseInt(this.qtyInput.value || '1', 10) : 1;
    const total = this.unitPrice() * qty;
    this.priceTarget.textContent = formatMoney(total);
  }

  async onSubmit(e) {
    e.preventDefault();
    const btn = this.form.querySelector('[type="submit"]');
    const original = btn ? btn.textContent : '';
    if (btn) { btn.setAttribute('disabled', 'true'); btn.textContent = 'Adding…'; }

    const formData = new FormData(this.form);
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Accept': 'application/json' },
        body: formData,
      });
      if (!res.ok) throw new Error('Add to cart failed');
      await res.json();
      await CartDrawer.refresh();
      CartDrawer.open();
    } catch (err) {
      console.error(err);
      window.location.href = '/cart';
    } finally {
      if (btn) { btn.removeAttribute('disabled'); btn.textContent = original; }
    }
  }
}
customElements.define('bloom-product-form', BloomProductForm);
/* ---------- Sticky add-to-cart bar ---------- */
document.addEventListener('DOMContentLoaded', () => {
  const bar = document.querySelector('[data-sticky-atc]');
  const anchor = document.querySelector('.product__atc');
  if (!bar || !anchor) return;

  const form = document.querySelector('bloom-product-form');

  // Show bar once the real button scrolls out of view
  const io = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        bar.setAttribute('data-visible', entry.isIntersecting ? 'false' : 'true');
      });
    },
    { threshold: 0, rootMargin: '0px' }
  );
  io.observe(anchor);

  // Mirror price + variant from the live form
  const priceEl = bar.querySelector('[data-sticky-price]');
  const variantEl = bar.querySelector('[data-sticky-variant]');
  const sync = () => {
    if (!form || !form.currentVariant) return;
    if (priceEl) priceEl.textContent = formatMoney(form.unitPrice());
    if (variantEl) variantEl.textContent = form.currentVariant.option1 || '';
  };
  new MutationObserver(sync).observe(document.querySelector('[data-atc-total]'), {
    childList: true,
    characterData: true,
    subtree: true,
  });

  // Reuse the real form's submit path
  bar.querySelector('[data-sticky-add]').addEventListener('click', () => {
    anchor.click();
  });
});

/* ---------- Gallery (thumbnail rail) ---------- */
class BloomGallery extends HTMLElement {
  connectedCallback() {
    this.main = this.querySelector('[data-gallery-main]');
    this.thumbs = Array.from(this.querySelectorAll('[data-gallery-thumb]'));
    this.thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => this.select(thumb));
    });
  }
  select(thumb) {
    this.thumbs.forEach((t) => t.setAttribute('data-active', t === thumb ? 'true' : 'false'));
    const src = thumb.getAttribute('data-media-src');
    const img = this.main.querySelector('img');
    if (img && src) { img.src = src; }
  }
  selectByMediaId(mediaId) {
    const match = this.thumbs.find((t) => t.getAttribute('data-media-id') === String(mediaId));
    if (match) this.select(match);
  }
}
customElements.define('bloom-gallery', BloomGallery);

/* ---------- Cart drawer ---------- */
const CartDrawer = {
  el: null,
  init() {
    this.el = document.querySelector('[data-cart-drawer]');
    if (!this.el) return;
    document.querySelectorAll('[data-cart-toggle]').forEach((btn) => {
      btn.addEventListener('click', (e) => { e.preventDefault(); this.open(); });
    });
    this.el.querySelectorAll('[data-cart-close]').forEach((btn) => {
      btn.addEventListener('click', () => this.close());
    });
    this.el.addEventListener('click', (e) => {
      if (e.target.hasAttribute('data-cart-overlay')) this.close();
    });
    this.bindLineItems();
  },
  open() { if (this.el) { this.el.setAttribute('data-open', 'true'); document.body.style.overflow = 'hidden'; } },
  close() { if (this.el) { this.el.setAttribute('data-open', 'false'); document.body.style.overflow = ''; } },
  bindLineItems() {
    if (!this.el) return;
    this.el.querySelectorAll('[data-line-remove]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const key = btn.getAttribute('data-line-remove');
        await this.changeLine(key, 0);
      });
    });
  },
  async changeLine(key, quantity) {
    try {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: key, quantity }),
      });
      if (!res.ok) throw new Error('Cart change failed');
      await this.refresh();
    } catch (err) {
      console.error(err);
    }
  },
  async refresh() {
    try {
      const res = await fetch('/?section_id=cart-drawer');
      const text = await res.text();
      const html = new DOMParser().parseFromString(text, 'text/html');
      const fresh = html.querySelector('[data-cart-drawer]');
      const current = document.querySelector('[data-cart-drawer]');
      if (fresh && current) {
        const wasOpen = current.getAttribute('data-open') === 'true';
        current.innerHTML = fresh.innerHTML;
        if (wasOpen) current.setAttribute('data-open', 'true');
        CartDrawer.el = current;
        CartDrawer.bindLineItems();
        current.querySelectorAll('[data-cart-close]').forEach((btn) => {
          btn.addEventListener('click', () => CartDrawer.close());
        });
      }
      // update header cart count
      const cartRes = await fetch('/cart.js');
      const cart = await cartRes.json();
      document.querySelectorAll('[data-cart-count]').forEach((el) => {
        el.textContent = cart.item_count;
      });
    } catch (err) {
      console.error(err);
    }
  },
};

document.addEventListener('DOMContentLoaded', () => CartDrawer.init());
window.CartDrawer = CartDrawer;
